<template>
  <div class="image-container">
    <!-- Zoom Controls -->
    <!-- <div class="zoom-controls">
      <button @click="zoomIn">➕ Zoom In</button>
      <button @click="zoomOut">➖ Zoom Out</button>
      <button @click="resetZoom">🔄 Reset</button>
    </div> -->

    <!-- Rotate Button -->

    <!-- Image Wrapper with Drag/Pan -->
    <div class="image-wrapper" ref="imageWrapper" @mousedown="startDrag" @mousemove="onDrag" @mouseup="stopDrag" @mouseleave="stopDrag">
      <div class="image-container-inner" :style="imageStyle">
        <div v-if="isRetentionExpired" style="display: flex; justify-content: center; align-items: center; padding: 50px; border-radius: 5px">
          <span>Retention Expired</span>
        </div>
        <div v-else>
          <img
            ref="imageRef"
            :src="imageSrcWithCache || props.imageSrc"
            alt="Highlighted Image"
            class="image"
            @load="onImageLoad"
            @mousemove="
              (e) => {
                if (props.magnifierEnabled) handleMouseMove(e)
              }
            "
            @mouseleave="props.magnifierEnabled ? handleMouseLeave : null"
            @contextmenu="handleRightClick"
          />
        </div>

        <!-- Magnifier Overlay -->
        <div
          v-if="props.magnifierEnabled && magnifierVisible && imageRef && imageRef.naturalWidth"
          class="magnifier"
          :style="{
            left: magnifierDisplayX - magnifierSize / 2 + 'px',
            top: magnifierDisplayY - magnifierSize / 2 + 'px',
            width: magnifierSize + 'px',
            height: magnifierSize + 'px',
            backgroundImage: `url('${imageSrcWithCache || props.imageSrc}')`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: `-${magnifierX * magnifierZoom - magnifierSize / 2}px -${magnifierY * magnifierZoom - magnifierSize / 2}px`,
            backgroundSize: `${imageRef.naturalWidth * magnifierZoom}px ${imageRef.naturalHeight * magnifierZoom}px`,
          }"
        >
          <div v-for="box in magnifierBoxStyles" :key="box.key" class="bounding-box magnifier-bounding-box" :style="box.style"></div>
        </div>
        <!-- Bounding Boxes -->
        <div v-for="box in boxStyles" :key="box.key" class="bounding-box" :style="box.style"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, defineProps, computed, defineExpose, nextTick, watch, defineEmits } from 'vue'
import { put } from '@/utils/api.js'
import { showToast } from '@/utils/toast'

// Props
const props = defineProps({
  textData: Array, // Bounding boxes array
  imageSrc: String, // Image URL
  magnifierEnabled: Boolean, // Magnifier toggle
  isExpanded: Boolean, // Magnifier toggle
  isRetentionExpired: Boolean, // Magnifier toggle
  imageId: Number, // mx_file_page_textract_details_id for rotation
})

const emit = defineEmits(['update:magnifierEnabled', 'imageRotated'])

// Refs
const imageRef = ref(null)
const imageWrapper = ref(null)
const zoomLevel = ref(1) // Default zoom level
const isDragging = ref(false)
const dragStartX = ref(0)
const dragStartY = ref(0)
const offsetX = ref(0)
const offsetY = ref(0)
const recalculationKey = ref(0)
const isRotating = ref(false)
const imageSrcWithCache = ref(null)

const onImageLoad = () => {
  recalculationKey.value++
}

const getBoxStyle = (boundingBox) => {
  if (!imageRef.value || !imageRef.value.naturalWidth || !imageRef.value.clientHeight) {
    return {}
  }

  const displayWidth = imageRef.value.clientWidth
  const displayHeight = imageRef.value.clientHeight
  const naturalWidth = imageRef.value.naturalWidth
  const naturalHeight = imageRef.value.naturalHeight

  const scaleX = displayWidth / naturalWidth
  const scaleY = displayHeight / naturalHeight

  const padding = 2 // px
  const boxLeft = boundingBox.Left * naturalWidth * scaleX - padding
  const boxTop = boundingBox.Top * naturalHeight * scaleY - padding
  const boxWidth = boundingBox.Width * naturalWidth * scaleX + padding * 2
  const boxHeight = boundingBox.Height * naturalHeight * scaleY + padding * 2

  return {
    position: 'absolute',
    left: `${boxLeft}px`,
    top: `${boxTop}px`,
    width: `${boxWidth}px`,
    height: `${boxHeight}px`,
    // border: '2px solid #28aef5',
    border: '1px solid rgb(56, 142, 60)',

    pointerEvents: 'none',
  }
}

const boxStyles = computed(() => {
  recalculationKey.value // Depend on the key
  if (!props.textData) {
    return []
  }
  const result = props.textData.map((box, index) => ({
    key: index,
    style: getBoxStyle(box.BoundingBox),
  }))
  return result
})

const magnifierBoxStyles = computed(() => {
  if (!props.textData || !imageRef.value || !magnifierVisible.value) return []
  const naturalWidth = imageRef.value.naturalWidth
  const naturalHeight = imageRef.value.naturalHeight
  // The top-left of the magnifier in natural image coordinates
  const magLeft = magnifierX.value - magnifierSize / 2 / magnifierZoom.value
  const magTop = magnifierY.value - magnifierSize / 2 / magnifierZoom.value
  // For each bounding box, calculate its position and size relative to the magnifier
  return props.textData
    .map((box, index) => {
      const bb = box.BoundingBox
      // Bounding box in natural image coordinates with padding
      const padding = 2 // px (same as main bounding boxes)
      const displayWidth = imageRef.value.clientWidth
      const scaleX = displayWidth / naturalWidth
      const paddingInNaturalCoords = padding / scaleX // Convert padding to natural coordinates

      const boxLeft = bb.Left * naturalWidth - paddingInNaturalCoords
      const boxTop = bb.Top * naturalHeight - paddingInNaturalCoords
      const boxWidth = bb.Width * naturalWidth + paddingInNaturalCoords * 2
      const boxHeight = bb.Height * naturalHeight + paddingInNaturalCoords * 2
      // Position relative to magnifier's top-left
      const relLeft = (boxLeft - magLeft) * magnifierZoom.value
      const relTop = (boxTop - magTop) * magnifierZoom.value
      const relWidth = boxWidth * magnifierZoom.value
      const relHeight = boxHeight * magnifierZoom.value
      // Only render if at least partially inside the magnifier
      if (relLeft + relWidth < 0 || relTop + relHeight < 0 || relLeft > magnifierSize || relTop > magnifierSize) {
        return null
      }
      return {
        key: index,
        style: {
          position: 'absolute',
          left: `${relLeft}px`,
          top: `${relTop}px`,
          width: `${relWidth}px`,
          height: `${relHeight}px`,
          border: '1.5px solid #388e3c',
          backgroundColor: '#40ff0066',
          pointerEvents: 'none',
          boxSizing: 'border-box',
        },
      }
    })
    .filter(Boolean)
})

let resizeObserver = null
onMounted(() => {
  if (imageWrapper.value) {
    resizeObserver = new ResizeObserver(() => {
      recalculationKey.value++
    })
    resizeObserver.observe(imageWrapper.value)
  }
})

onUnmounted(() => {
  if (resizeObserver && imageWrapper.value) {
    resizeObserver.unobserve(imageWrapper.value)
  }
})

//Zoom functions
const zoomIn = () => {
  zoomLevel.value = Math.min(zoomLevel.value + 1, 5) // Maximum 5x zoom
}
const zoomOut = () => {
  zoomLevel.value = Math.max(zoomLevel.value - 0.5, 1) // Minimum 1x zoom
}
const resetZoom = () => {
  zoomLevel.value = 1
  offsetX.value = 0
  offsetY.value = 0
}

// Drag Functions
const startDrag = (event) => {
  if (zoomLevel.value > 1) {
    isDragging.value = true
    dragStartX.value = event.clientX - offsetX.value
    dragStartY.value = event.clientY - offsetY.value
  }
}

const onDrag = (event) => {
  if (isDragging.value) {
    offsetX.value = event.clientX - dragStartX.value
    offsetY.value = event.clientY - dragStartY.value
  }
}

const stopDrag = () => {
  isDragging.value = false
}

// Magnifier logic
const magnifierVisible = ref(false)
const magnifierX = ref(0)
const magnifierY = ref(0)
const magnifierSize = 240 // px (increased from 180)
const magnifierZoom = computed(() => (props.isExpanded ? 2 : 1.5))
const magnifierDisplayX = ref(0)
const magnifierDisplayY = ref(0)

const handleMouseMove = (event) => {
  if (!props.magnifierEnabled || !imageRef.value) return
  const rect = imageRef.value.getBoundingClientRect()

  // Mouse position relative to displayed image
  const x = event.clientX - rect.left
  const y = event.clientY - rect.top

  // Displayed and natural sizes
  const displayWidth = imageRef.value.clientWidth
  const displayHeight = imageRef.value.clientHeight
  const naturalWidth = imageRef.value.naturalWidth
  const naturalHeight = imageRef.value.naturalHeight

  const scaleX = naturalWidth / displayWidth
  const scaleY = naturalHeight / displayHeight

  // Map to natural image coordinates for background
  magnifierX.value = x * scaleX
  magnifierY.value = y * scaleY
  // Store display coordinates for overlay position
  magnifierDisplayX.value = x
  magnifierDisplayY.value = y
  magnifierVisible.value = true
}
const handleMouseLeave = () => {
  magnifierVisible.value = false
}

const handleRightClick = (event) => {
  event.preventDefault()
  magnifierVisible.value = false
  emit('update:magnifierEnabled', false)
}

// Rotate Image Function
const rotateImage = async () => {
  showToast({
    title: 'Rotating image...',
    message: 'Please wait while we rotate the image.',
    type: 'success',
  })
  if (!props.imageId || isRotating.value) return

  try {
    isRotating.value = true
    const payload = [
      {
        mx_file_page_textract_details_id: props.imageId,
        rotation_angle: 270,
      },
    ]

    const response = await put('/v1/s3/rotate-image', payload)
    console.log('Rotate response==>', response)

    // Refresh image by adding cache-busting parameter
    if (props.imageSrc) {
      const separator = props.imageSrc.includes('?') ? '&' : '?'
      imageSrcWithCache.value = `${props.imageSrc}${separator}t=${Date.now()}`
    }

    // Emit event to parent to refresh image data if needed
    emit('imageRotated', response)
  } catch (error) {
    // console.error('Error rotating image:', error)
    // You might want to show a toast/notification here
  } finally {
    isRotating.value = false
  }
}

// Compute image style based on zoom and panning
const imageStyle = computed(() => ({
  transform: `scale(${zoomLevel.value}) translate(${offsetX.value}px, ${offsetY.value}px)`,
  transformOrigin: 'top left',
  transition: isDragging.value ? 'none' : 'transform 0.3s ease-in-out',
  cursor: zoomLevel.value > 1 ? 'grab' : 'default',
}))

const forceRecalculate = () => {
  nextTick(() => {
    recalculationKey.value++
  })
}

const forceReloadImage = (imageUrl = null) => {
  // Clear the cache to force image reload
  imageSrcWithCache.value = null
  // Trigger recalculation
  nextTick(() => {
    recalculationKey.value++
    // Force image reload with cache-busting
    // Use provided imageUrl or fallback to props.imageSrc
    const imageSource = imageUrl || props.imageSrc
    if (imageSource) {
      const separator = imageSource.includes('?') ? '&' : '?'
      imageSrcWithCache.value = `${imageSource.split('?')[0]}${separator}t=${Date.now()}`
    }
  })
}

// Expose zoom functions and rotate function to parent component
defineExpose({
  zoomIn,
  zoomOut,
  resetZoom,
  forceRecalculate,
  rotateImage,
  forceReloadImage,
})

watch(
  () => props.magnifierEnabled,
  () => {
    // Watch for magnifier enabled changes if needed
  }
)

// Watch for imageSrc changes - reset cache when source changes
watch(
  () => props.imageSrc,
  () => {
    // Reset cache when image source changes (new image selected)
    // Only use cache-busted version after rotation
    imageSrcWithCache.value = null
  }
)
</script>

<style scoped>
.image-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

/* Zoom Controls */
.zoom-controls {
  margin-bottom: 10px;
}

.zoom-controls button {
  margin: 5px;
  padding: 10px;
  font-size: 16px;
  cursor: pointer;
  border: none;
  border-radius: 5px;
  background-color: #1976d2;
  color: white;
  height: 50%;
}

/* Image Wrapper */
.image-wrapper {
  position: relative;
  overflow-y: auto;
  display: inline-block;
  width: 100%;
  height: 100%;
  text-align: center;
  cursor: grab;
}

/* Image Container Inner */
.image-container-inner {
  position: relative;
  display: inline-block;
}

/* Image */
.image {
  max-width: 100%;
  height: auto;
  display: block;
}

/* Bounding Box */
.bounding-box {
  position: absolute;
  border: 0px !important;
  /* background-color: rgba(144, 238, 144, 0.4); Light green with 30% opacity */
  background-color: #40ff0066;
}

.magnifier-bounding-box {
  border: 1.5px solid #388e3c !important;
  background-color: #40ff0066 !important;
  pointer-events: none;
  box-sizing: border-box;
}

.magnifier {
  position: absolute;
  pointer-events: none;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  border: 2px solid #1976d2;
  z-index: 10;
  overflow: hidden;
  background-color: transparent;
}

/* Rotate Controls */
.rotate-controls {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 100;
}

.rotate-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.rotate-button:hover:not(:disabled) {
  background-color: var(--primary-color);
}

.rotate-button:disabled {
  background-color: var(--primary-color);
  cursor: not-allowed;
  opacity: 0.7;
}

.rotate-button svg {
  width: 20px;
  height: 20px;
}
</style>
